import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { AdminContext } from '../context/AdminContext';
import { BankContext } from '../context/BankContext';
import UserDeleteToast from '../components/UserDeleteToast';
import PaginationNavigation from './paginationNavigation';
import PaginationControls from './paginationControls';
import DynamiCFormModal from '../components/DynamicFormModal';
import UserHistory from '../components/UserHistory';
import { useNavigate } from 'react-router-dom';
import { History, ChevronLeft, Users, UserCheck, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const {
    getAllUser,
    updateUser,
    paginatedUser,
    isLoading,
    deleteUser,
    totalPages,
    totalUsers,
    currentPage,
    userPerPage,
    setUserPerPage,
    movetoPage,
    goPrevPage,
    goNextPage,
    users
  } = useContext(AdminContext);

  const { getBank } = useContext(BankContext);
  const [formError, setError] = useState({});
  const [editingForm, setEditingForm] = useState({
    name: '',
    email: '',
    startDate: '',
    endDate: '',
    cost: 0,
    password: ''
  });
  const [selectedUserHistory, setSelectedUserHistory] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Total');
  const [searchQuery, setSearchQuery] = useState('');
  const endDateRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      await getAllUser();
    };
    fetchUser();
  }, []);

  // 🟢 Filter counts
  const activeCount = useMemo(() => {
    if (!paginatedUser) return 0;
    return paginatedUser.filter(user => user.status === 'Active').length;
  }, [paginatedUser]);

  const suspendedCount = useMemo(() => {
    if (!paginatedUser) return 0;
    return paginatedUser.filter(user => user.status === 'Suspended').length;
  }, [paginatedUser]);

  const inActiveCount = useMemo(() => {
    if (!paginatedUser) return 0;
    return paginatedUser.filter(user => user.status === 'Inactive').length;
  }, [paginatedUser]);

  // 🟡 Filter + Search logic
  const displayUsers = useMemo(() => {
    if (!paginatedUser) return [];

    let filteredList =
      activeFilter === 'Total'
        ? paginatedUser
        : paginatedUser.filter(user => user.status === activeFilter);

    if (searchQuery.trim() === '') return filteredList;

    const lowerCaseQuery = searchQuery.toLowerCase();
    return users.filter(user => {
      const nameMatch = user.name?.toLowerCase().includes(lowerCaseQuery);
      const emailMatch = user.email?.toLowerCase().includes(lowerCaseQuery);
      return nameMatch || emailMatch;
    });
  }, [paginatedUser, activeFilter, searchQuery]);

  // 🟢 Handle Edit
  const handleEditUser = user => {
    setError({});
    const latestMembership =
      user.membershipsDetails?.length > 0
        ? user.membershipsDetails[user.membershipsDetails.length - 1]
        : {};

    const formatedStartDate = latestMembership.startDate
      ? new Date(latestMembership.startDate).toISOString().slice(0, 10)
      : '';
    const formatedEndDate = latestMembership.endDate
      ? new Date(latestMembership.endDate).toISOString().slice(0, 10)
      : '';

    setEditingForm({
      ...user,
      cost: 0,
      startDate: formatedStartDate,
      endDate: formatedEndDate,
      password: ''
    });

    if (endDateRef.current) {
      if (formatedStartDate) {
        const minEndDate = new Date(formatedStartDate);
        minEndDate.setDate(minEndDate.getDate() + 1);
        endDateRef.current.min = minEndDate.toISOString().slice(0, 10);
      } else {
        endDateRef.current.min = '';
      }
    }

    document.getElementById('edit-user-modal').showModal();
  };

  // 🟡 Handle Form Input
  const handleFormChange = e => {
    const { name, value } = e.target;
    setEditingForm(prev => ({ ...prev, [name]: value }));

    if (name === 'startDate' && endDateRef.current) {
      const newMinDate = new Date(value);
      newMinDate.setDate(newMinDate.getDate() + 1);
      endDateRef.current.min = newMinDate.toISOString().slice(0, 10);
    }
  };

  // 🟢 Submit Edit
  const handleSubmit = async e => {
    e.preventDefault();
    document.getElementById('edit-user-modal').close();

    const updatedData = {
      _id: editingForm._id,
      name: editingForm.name,
      email: editingForm.email,
      startDate: editingForm.startDate,
      endDate: editingForm.endDate,
      ...(editingForm.password ? { password: editingForm.password } : {})
    };

    await updateUser(updatedData);
    await getBank();
    setEditingForm({ name: '', email: '', startDate: '', endDate: '', cost: 0, password: '' });
  };

  // 🟠 Handle Status Change
  const handleStatusChange = async (e, user) => {
    const newStatus = e.target.value;
    await updateUser({ _id: user._id, status: newStatus });
  };

  // 📜 Handle History Modal
  const handleHistory = user => {
    setSelectedUserHistory(user.membershipsDetails || []);
    document.getElementById('show_hisory_modal').showModal();
  };

  // 🗑️ Handle Delete
  const handleDelete = userId => {
    const performDelete = async () => {
      await deleteUser(userId);
    };
    toast.warn(<UserDeleteToast onDelete={performDelete} />, {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
      position: 'top-right'
    });
  };

  // 🧩 UI Rendering
  return (
    <div className="bg-gray-50 min-h-screen relative m-2">
      <div className="absolute top-8 left-2">
        {/* Back Button */}
        <button
          onClick={() => navigate("/library-admin")}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors group"
        >
          <svg
            className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span className="font-medium">Back to Dashboard</span>
        </button>

      </div>

    


      <div className="pt-24 sm:pt-16 pb-8 pl-2 pr-2 text-gray-700  font-medium text-3xl text-center">
        User And Subscription Management
      </div>

      {/* Summary Cards */}
      <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Customers', value: totalUsers, color: 'blue', icon: Users },
            { label: 'Active Customers', value: activeCount, color: 'green', icon: UserCheck },
            { label: 'Suspended Customers', value: suspendedCount, color: 'yellow', icon: AlertTriangle },
            { label: 'Inactive Customers', value: inActiveCount, color: 'red', icon: XCircle }
          ].map((card, idx) => {
            const Icon = card.icon;
            return (
              <div
                key={idx}
                onClick={() => setActiveFilter(card.label.split(' ')[0])}
                // --- ADDED CLASSES HERE ---
                className={`bg-white p-6 cursor-pointer transition-all hover:-translate-y-1 rounded-lg shadow-lg flex items-center justify-between border-l-[5px] border-${card.color}-600`}
              >
                <div>
                  <span className="text-sm font-medium text-gray-500">{card.label}</span>
                  <p className={`text-3xl font-bold text-${card.color}-600`}>
                    {isLoading ? '...' : card.value}
                  </p>
                </div>
                <div className={`p-3 rounded-full bg-${card.color}-100 text-${card.color}-600`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            );
        })}
      </div>
      {/* Search + Table */}
      <div className="p-4">
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto ">
      <div className="p-5 inline-block min-w-full align-middle">
        <input
          type="text"
          placeholder="Search by name or email"
          className="w-full px-4 py-2 border rounded-lg shadow-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />

        <PaginationControls
          totalPages={totalPages}
          currentPage={currentPage}
          totalUsers={totalUsers}
          setUserPerPage={setUserPerPage}
          userPerPage={userPerPage}
        />

        {isLoading ? (
          <p className="text-gray-600 text-center mt-8 text-sm">Loading...</p>
        ) : displayUsers.length === 0 ? (
          <h1 className="text-center text-gray-500 font-medium text-2xl">No Users Found</h1>
        ) : (
          <table className="table">
            <thead className="table-heading  table-heading-bg">
              <tr>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">SR.NO</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">NAME</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">EMAIL</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">START DATE</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">END DATE</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left ">STATUS</th>
                <th className="py-3 px-4 text-xs font-semibold tracking-wider text-left">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {displayUsers.map((user, index) => (
                <tr key={user._id}>
                <td className="text-center align-middle">{index + 1}</td>
                <td className="text-center align-middle">{user.name}</td>
                <td className="text-center align-middle">{user.email}</td>
                <td className="text-center align-middle">
                    {user.membershipsDetails?.length
                      ? new Date(
                          user.membershipsDetails[user.membershipsDetails.length - 1]?.startDate
                        ).toLocaleDateString('en-GB')
                      : '-'}
                  </td>
                  <td className='text-center align-middle'>
                    {user.membershipsDetails?.length
                      ? new Date(
                          user.membershipsDetails[user.membershipsDetails.length - 1]?.endDate
                        ).toLocaleDateString('en-GB')
                      : '-'}
                  </td>
                  <td className='text-center align-middle'>
                    <select
                      className={`rounded-md px-2 py-1 ${
                        user.status === 'Active'
                          ? 'bg-green-100 text-green-500'
                          : user.status === 'Suspended'
                          ? 'text-yellow-500 bg-yellow-100'
                          : 'text-red-500 bg-red-100'
                      }`}
                      onChange={e => handleStatusChange(e, user)}
                      value={user.status}
                      name="status"
                    >
                      <option value="Active">Active</option>
                      <option value="Suspended">Suspended</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </td>
                  <td className="py-4 text-center px-4 flex space-x-2 justify-center">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="px-5 py-2 text-sm text-white bg-gray-700 hover:bg-gray-800 rounded-full"
                    >
                      Edit
                    </button>
                    <button className="p-2" onClick={() => handleHistory(user)}>
                      <History className="w-6 h-6 cursor-pointer" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <PaginationNavigation
          totalPages={totalPages}
          currentPage={currentPage}
          movetoPage={movetoPage}
          goPrevPage={goPrevPage}
          goNextPage={goNextPage}
        />
      </div>
      </div>
      </div>

      {/* Edit Modal */}
      <DynamiCFormModal id="edit-user-modal" title="Edit User">
        <form onSubmit={handleSubmit} className="flex flex-col justify-center space-y-3">
          <label className="modal-form-lable" htmlFor="name">
            Name
          </label>
          <input
            className="modal-form-input"
            type="text"
            name="name"
            value={editingForm.name}
            onChange={handleFormChange}
          />

          <label className="modal-form-lable" htmlFor="email">
            Email
          </label>
          <input
            className="modal-form-input"
            type="email"
            name="email"
            value={editingForm.email}
            onChange={handleFormChange}
          />

          <label className="modal-form-lable" htmlFor="startDate">
            Start Date
          </label>
          <input
            className="modal-form-input"
            type="date"
            name="startDate"
            value={editingForm.startDate}
            onChange={handleFormChange}
          />

          <label className="modal-form-lable" htmlFor="endDate">
            End Date
          </label>
          <input
            className="modal-form-input"
            type="date"
            name="endDate"
            ref={endDateRef}
            value={editingForm.endDate}
            onChange={handleFormChange}
          />

          <label className="modal-form-lable" htmlFor="password">
            Change Password (optional)
          </label>
          <input
            className="modal-form-input"
            type="password"
            name="password"
            placeholder="Enter new password (optional)"
            value={editingForm.password}
            onChange={handleFormChange}
          />

          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              className="w-fit bg-green-500 hover:bg-green-600 text-gray-100 px-4 py-2 rounded-md"
            >
              Save Changes
            </button>
          </div>
        </form>
      </DynamiCFormModal>

      {/* History Modal */}
      <DynamiCFormModal id="show_hisory_modal" title="User History">
        <UserHistory history={selectedUserHistory} />
      </DynamiCFormModal>
    </div>
  );
};

export default UserManagement;
